<script setup lang="ts">
import { onMounted, type Component } from 'vue'

import { currentLocation } from '@client/elements'
import { isFormValid } from '@client/elements/address'

import type { Location } from '@/types'

export interface AccordionObject<T = any> {
	id: string
	title: string
	icon: string
	courier: string
	content: T
	component: Component
}

const accordion = defineModel<AccordionObject<Location>[]>('accordion')

defineProps<{ callback: (content: Location, icon: string, courier: string) => void }>()

function selectFirstElement() {
	if (accordion.value?.length && !currentLocation.value) {
		currentLocation.value = accordion.value[0].content
	}
}

onMounted(selectFirstElement)
</script>

<template>
	<div class="__accordion_container">
		<template v-if="accordion && accordion.length">
			<template v-for="({ id, title, icon, content, component, courier }, index) in accordion" :key="index">
				<div :id="id" :data-id="id" :data-icon="icon" :data-courier="courier" class="__accordion" tabindex="0"
					:class="{ active: currentLocation?.id === id }" @click.prevent="callback(content, icon, courier)">
					<div class="__header">
						<img class="__icon" :src="icon" alt="icon" />
						<div class="__title" :title="title">{{ title }}</div>
						<template v-if="currentLocation?.id !== id">
							<svg class="__courier" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="14">
								<path
									d="M248 72c0-13.3-10.7-24-24-24s-24 10.7-24 24V232H40c-13.3 0-24 10.7-24 24s10.7 24 24 24H200V440c0 13.3 10.7 24 24 24s24-10.7 24-24V280H408c13.3 0 24-10.7 24-24s-10.7-24-24-24H248V72z" />
							</svg>
						</template>
						<template v-else>
							<svg class="__courier" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="16">
								<path fill="#39a953"
									d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z" />
							</svg>
						</template>
					</div>
					<component v-if="currentLocation?.id === id" :is="component" :location="content" />
				</div>
			</template>
		</template>
		<template v-else-if="isFormValid">
			<div class="__empty">
				<span>Geen address ingevuld, of adres niet beschikbaar.</span>
			</div>
		</template>
		<template v-else>
			<div class="__empty">
				<span>Geen locatie gevonden.</span>
			</div>
		</template>
	</div>
</template>
